import { Experiment, type ExperimentClient } from '@amplitude/experiment-js-client'
import { createContext, useMemo, type ReactNode } from 'react'

import config from '@src/config'

interface ExperimentContextValue {
  client: ExperimentClient | null
  error: unknown
}

const ExperimentContext = createContext<ExperimentContextValue | null>(null)

interface ExperimentProviderProps {
  children: ReactNode
}

/**
 * Provider that initializes and exposes the Amplitude Experiment client.
 * Must be mounted inside AnalyticsProvider — the experiment client integrates
 * with the Amplitude Analytics SDK for shared user identity.
 */
export function ExperimentProvider({ children }: ExperimentProviderProps) {
  const deploymentKey = config.AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY

  // eslint-disable-next-line custom-rules/no-memoization-in-odyssey -- TODO: Remove memoization; Odyssey relies on React Compiler (UXF-1509)
  const { client, error } = useMemo<ExperimentContextValue>(() => {
    if (!deploymentKey) {
      return {
        client: null,
        error: new Error('No Amplitude experiment deployment key configured'),
      }
    }
    return {
      client: Experiment.initializeWithAmplitudeAnalytics(deploymentKey),
      error: undefined,
    }
  }, [deploymentKey])

  return (
    <ExperimentContext.Provider value={{ client, error }}>
      {children}
    </ExperimentContext.Provider>
  )
}

export default ExperimentContext
